import { useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import { selectIsGpsConfigured } from "@/reducers/gpsConfigSelectors"
import { selectLastLocationReceivedAt } from "@/reducers/locationsSelectors"
import {
  findRouteConfig,
  selectIsCarConfigured,
} from "@/reducers/routes2gSlice"
import { isDriverRoomDataReady } from "@/reducers/roomSyncStatusSelectors"
import { useStalenessTick } from "@/hooks/useStalenessTick"
import { computeGpsIssues } from "@/lib/gpsIssues"

export type DriverRoomIssues = {
  gpsNotConfigured: boolean
  gpsStale: boolean
  gpsNeverReceived: boolean
  carNotConfigured: boolean
  hasAnyIssue: boolean
  lastReceivedAt: number | undefined
}

export const useDriverRoomIssues = (
  chatJid: string | undefined,
): DriverRoomIssues | null => {
  const dataReady = useAppSelector(isDriverRoomDataReady)

  const routeJidSelector = useMemo(
    () => selectRouteJidFromChatJid(chatJid ?? ""),
    [chatJid],
  )
  const routeJid = useAppSelector(routeJidSelector)

  const isGpsConfiguredSelector = useMemo(
    () => selectIsGpsConfigured(routeJid),
    [routeJid],
  )
  const isGpsConfigured = useAppSelector(isGpsConfiguredSelector)

  const lastReceivedAtSelector = useMemo(
    () => selectLastLocationReceivedAt(routeJid),
    [routeJid],
  )
  const lastReceivedAt = useAppSelector(lastReceivedAtSelector)

  const carRouteConfigSelector = useMemo(
    () => findRouteConfig(routeJid),
    [routeJid],
  )
  const carRouteConfig = useAppSelector(carRouteConfigSelector)

  const isCarConfiguredSelector = useMemo(
    () => selectIsCarConfigured(routeJid),
    [routeJid],
  )
  const isCarConfigured = useAppSelector(isCarConfiguredSelector)

  const now = useStalenessTick()

  return useMemo(() => {
    if (!chatJid || !routeJid) return null
    // Suppress warnings until init sync AND the post-sync gpsConfigs / locations
    // fetches have completed — otherwise we'd false-positive in the brief
    // window between init-sync-done and those slices being populated.
    if (!dataReady) return null

    const { gpsNotConfigured, gpsNeverReceived, gpsStale } = computeGpsIssues({
      isGpsConfigured,
      lastReceivedAt,
      now,
    })

    // Only flag car-not-configured when we actually have config data to evaluate.
    // Missing carRouteConfig means we have not fetched yet — avoid false positive.
    const carNotConfigured = carRouteConfig !== undefined && !isCarConfigured

    const hasAnyIssue =
      gpsNotConfigured || gpsStale || gpsNeverReceived || carNotConfigured

    return {
      gpsNotConfigured,
      gpsStale,
      gpsNeverReceived,
      carNotConfigured,
      hasAnyIssue,
      lastReceivedAt,
    }
  }, [
    chatJid,
    routeJid,
    dataReady,
    isGpsConfigured,
    lastReceivedAt,
    carRouteConfig,
    isCarConfigured,
    now,
  ])
}
